/** แต้มฝึกปรือ: the single progression currency, replacing XP and character levels. */
export const CULTIVATION = {
  /** Per second online, paid out every `tickSeconds` (30 a tick standing, 120 meditating). */
  idleRate: 0.5,
  meditateRate: 4,
  tickSeconds: 30,
  offlineRate: 0.1,
  offlineCapSeconds: 4 * 3600,
} as const;

export const MAX_SKILL_LEVEL = 10;

/** Cultivation cost to raise a skill from `level` to `level + 1`. */
export const skillCost = (level: number): number => Math.round(40 * level ** 1.5);

/** Cultivation cost to raise a neigong from `level` to `level + 1`. */
export const neigongCost = (level: number): number => Math.round(60 * level ** 1.4);

/** Damage multiplier for a skill at the given level (level 1 = ×1). */
export const skillPowerMult = (level: number): number => 1 + 0.08 * (Math.max(1, level) - 1);

/** Points earned while the game was closed. */
export function offlineGain(elapsedSeconds: number): number {
  if (!(elapsedSeconds > 0)) return 0;
  return Math.floor(Math.min(elapsedSeconds, CULTIVATION.offlineCapSeconds) * CULTIVATION.offlineRate);
}

/**
 * Martial power ranks (ระดับพลังฝีมือ) in place of levels: realms (境界), each of nine stages.
 * Add a realm by adding a line here — the cultivation each stage needs comes from a formula, so
 * nothing else changes.
 */
export const REALMS: readonly { readonly name: string; readonly glyph: string }[] = [
  { name: 'ฝึกกาย', glyph: '煉體' },
  { name: 'ฝึกลมปราณ', glyph: '煉氣' },
  { name: 'ยอดฝีมือชั้นสาม', glyph: '三流' },
  { name: 'ยอดฝีมือชั้นสอง', glyph: '二流' },
  { name: 'ยอดฝีมือชั้นหนึ่ง', glyph: '一流' },
  { name: 'ยอดฝีมือไร้เทียมทาน', glyph: '絕頂' },
  { name: 'ปรมาจารย์', glyph: '宗師' },
];

/**
 * Realms 8–12 (docs/LORE.md, rank names), joined to `REALMS` one at a time by the Major patch of
 * their year, together with somewhere new to spend cultivation. Not reachable yet; their glyphs are
 * already in UI_GLYPHS so the font subset is ready.
 */
export const REALMS_TO_COME: readonly {
  readonly name: string;
  readonly glyph: string;
  readonly year: number;
}[] = [
  { name: 'มหาปรมาจารย์', glyph: '大宗師', year: 1 },
  { name: 'ขั้นแปรเปลี่ยน', glyph: '化境', year: 3 },
  { name: 'ขั้นคืนสู่สามัญ', glyph: '歸真', year: 5 },
  { name: 'ขั้นฟ้าคนเป็นหนึ่ง', glyph: '天人', year: 7 },
  { name: 'ร่วมทาง', glyph: '同道', year: 9 },
];

/** Stages in every realm. */
export const STAGES = 9;

/** Cultivation the k-th stage (counting from 0 across all realms) takes, growing 10% a stage. */
const stageCost = (k: number) => Math.round(40 * 1.1 ** k);

/** Total cultivation spent needed to reach overall stage k. */
export function rankThreshold(k: number): number {
  let t = 0;
  for (let i = 0; i < k; i++) t += stageCost(i);
  return t;
}

/** The last overall stage there is. */
export const MAX_RANK = REALMS.length * STAGES - 1;

/** Name of an overall stage, e.g. "ยอดฝีมือชั้นสาม · ชั้น 4". */
export function rankName(k: number): string {
  const i = Math.max(0, Math.min(MAX_RANK, k));
  return `${REALMS[Math.floor(i / STAGES)]!.name} · ชั้น ${(i % STAGES) + 1}`;
}

export interface Rank {
  /** Overall stage across all realms (0 = ฝึกกาย ชั้น 1). */
  index: number;
  realm: string;
  stage: number;
  name: string;
  /** Cultivation spent needed for the next stage (null at the top). */
  next: number | null;
}

/** Power rank from total cultivation spent. */
export function rankFor(spent: number): Rank {
  let k = 0;
  while (k < MAX_RANK && spent >= rankThreshold(k + 1)) k++;
  return {
    index: k,
    realm: REALMS[Math.floor(k / STAGES)]!.name,
    stage: (k % STAGES) + 1,
    name: rankName(k),
    next: k < MAX_RANK ? rankThreshold(k + 1) : null,
  };
}

/** A monster's power rank, from its level. */
export const monsterRank = (level: number): number =>
  Math.max(0, Math.min(MAX_RANK, Math.round(level * 1.9)));

/** How dangerous a foe is next to the player: well below, about even, or above. */
export function threat(foe: number, player: number): 'low' | 'even' | 'high' {
  return foe <= player - 4 ? 'low' : foe >= player + 4 ? 'high' : 'even';
}

/**
 * Combat qi pool (ลมปราณสะสม, docs/design/2026-10-11-gameplay-loop.md Fix A, systems/qiPool.ts): each kill pays `direct` of its cultivation reward at once and
 * adds `fill` x the rest to a pool (capped at `cap`); meditating converts the pool at `CULTIVATION.meditateRate`
 * a second, and with the pool empty meditation pays only `emptyRate` a second. `standRate` is the standing/fighting
 * idle pay, which replaces `CULTIVATION.idleRate` (the 8-week target to the cap needs kills to be the main income).
 */
export const QI_POOL = { direct: 0.15, fill: 0.3, cap: 20000, standRate: 0.1, emptyRate: 0.2 } as const;
